<template>
  <div class="rentalObjectMeterVerification">
    <hl-ModalV2
      v-if="isElectricityMeterModalVisible"
      title="Bekreft strømmåler"
      titleIcon="budicon-alert-sign"
      is-closable
      @close="isElectricityMeterModalVisible = false"
    >
      <strong>Adresse:</strong>
      <p>
        {{ verifiedMeterData.address.street }} {{ verifiedMeterData.address.number }},
        {{ verifiedMeterData.address.postalCode }} {{ verifiedMeterData.address.city }}
      </p>

      <strong>Målernummer:</strong>
      <p>
        {{ verifiedMeterData.meterNumber }}
      </p>

      <strong>Målerpunkt-ID:</strong>
      <p>
        {{ verifiedMeterData.meterId }}
      </p>

      <p v-if="storeMeterDataError" class="text-danger">
        {{ storeMeterDataError }}
      </p>

      <template v-slot:buttons>
        <hl-Button class="mt-3" variant="secondary" outline @click="isElectricityMeterModalVisible = false">
          Avbryt
        </hl-Button>

        <hl-Button class="mt-3" @click="storeMeterData">
          <div class="d-flex align-items-center">
            <hl-LoaderDots v-if="isLoading" variant="white" class="mr-2" />
            Det stemmer!
          </div>
        </hl-Button>
      </template>
    </hl-ModalV2>

    <BlockContainer class="p-5">
      <template v-if="!isMeterNumberDataStored || isChangingMeterData">
        <h5>Registrer strømmåler for leieobjektet</h5>

        <p class="mt-4 mb-0">
          Skriv inn målernummeret (finnes på måleren) eller målerpunkt-ID (står på strømfakturaen).
        </p>

        <p>
          <a
            href="https://husleie-no.zendesk.com/hc/no/articles/18187907170844-Hvordan-finner-jeg-m%C3%A5lernummeret"
            rel="noopener"
            target="_blank"
          >
            Les mer om hvordan du finner dem her.
          </a>
        </p>

        <hl-Input type="number" v-model="electricityMeterNumber" placeholder="7075000038212315" />

        <hl-Button :disabled="!electricityMeterNumber" class="mt-3" @click="verifyMeterData">
          <div class="d-flex align-items-center">
            <hl-LoaderDots v-if="isLoading" variant="white" class="mr-2" />
            Registrer strømmåler
          </div>
        </hl-Button>

        <p v-if="verifyMeterDataError" class="text-danger mt-3">
          {{ verifyMeterDataError }}
        </p>
      </template>

      <template v-else>
        <h5>Strømmåler</h5>

        <div class="d-flex mt-4">
          <strong>Målernummer</strong>
          <span class="ml-5">
            {{ rentalObject?.details?.electricityMeter }}
          </span>
        </div>

        <div class="d-flex mt-2">
          <strong>Målerpunkt ID</strong>
          <span class="ml-5">
            {{ rentalObject?.details?.electricityMeterId }}
          </span>
        </div>

        <hl-Button class="mt-4" @click="isChangingMeterData = true"> Endre </hl-Button>
      </template>
    </BlockContainer>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import BlockContainer from '@/components/BlockContainer.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import ModalV2 from '@modules/husleie-framework/src/components/modals/ModalV2.vue';
import LoaderDots from '@modules/husleie-framework/src/components/loaders/LoaderDots';
import { ElectricityApi } from '@modules/husleieapi2';

export default Vue.extend({
  name: 'RentalObjectMeterVerification',
  data() {
    return {
      electricityMeterNumber: undefined,
      verifiedMeterData: undefined,
      isElectricityMeterModalVisible: false,
      isChangingMeterData: false,
      isVerifyingMeterData: false,
      verifyMeterDataError: undefined,
      storeMeterDataError: undefined,
      isLoading: false,
    };
  },
  props: {
    rentalObjectId: {
      type: String,
      required: true,
    },
  },
  components: {
    BlockContainer,
    'hl-Input': Input,
    'hl-Button': Button,
    'hl-ModalV2': ModalV2,
    'hl-LoaderDots': LoaderDots,
  },
  computed: {
    ...mapGetters(['getRentalObject']),

    rentalObject() {
      return this.getRentalObject(this.rentalObjectId);
    },
    isMeterNumberDataStored() {
      return this.rentalObject?.details?.electricityMeter && this.rentalObject?.details?.electricityMeterId;
    },
  },
  methods: {
    ...mapActions(['patchRentalObject']),

    async verifyMeterData() {
      const api = new ElectricityApi();

      this.isLoading = true;
      this.verifyMeterDataError = undefined;

      try {
        this.verifiedMeterData = await api.verifyElectricityMeterData({
          body: {
            rentalObjectId: this.rentalObjectId,
            electricityMeterNumber: this.electricityMeterNumber,
          },
        });
        this.isElectricityMeterModalVisible = true;
      } catch {
        this.verifyMeterDataError = 'En feil oppstod. Prøv igjen om litt';
      } finally {
        this.isLoading = false;
      }
    },
    async storeMeterData() {
      this.isLoading = true;
      this.storeMeterDataError = false;

      this.patchRentalObject({
        rentalObjectId: this.rentalObjectId,
        body: {
          details: {
            ...this.rentalObject.details,
            electricityMeter: this.verifiedMeterData.meterNumber,
            electricityMeterId: this.verifiedMeterData.meterId,
          },
        },
        success: () => {
          this.isLoading = false;
          this.isElectricityMeterModalVisible = false;
          this.isChangingMeterData = false;
        },
        error: () => {
          this.isLoading = false;
          this.storeMeterDataError = 'En feil oppstod. Prøv igjen om litt';
        },
      });
    },
  },
});
</script>
